// src/encounter/services/use-encounter.ts
// Encounter lifecycle (Flutter: FetchEncounterByAppointmentIdEvent → CreateEncounterEvent).
import { useMutation, useQuery } from '@tanstack/react-query';
import { get, postText, ApiError } from '@/shared/api/api-client';
import type { EncounterModel, CreateEncounterRequest } from '@/types/encounter-api';

/**
 * The encounter's TWO-PHASE lifecycle, as Flutter runs it:
 *
 *   Initial  — written when the pre-encounter OPENS and no encounter exists yet
 *              (`patient_encounter_bloc.dart:401-431`: GetEncounterByEncounterId 404 →
 *              `createEncounter(automaticMoveToAppointment: false, status: Initial)`).
 *              The view does NOT move; this exists purely so an encounter id is in hand.
 *   Draft    — written when Start is pressed (`startEncounter()`, :2555-2578, which calls
 *              `createEncounter(status: Draft)` when the current status is Initial).
 *   Completed— written on sign.
 *
 * WHY THE OPEN-PHASE EXISTS (Andrew, 2026-08-28): "its called when opening the screen as we
 * need to know the vitals, patient history items added in which encounter and we need the
 * encounterId." Vitals and history items recorded during the pre-encounter have to attach to
 * an encounter, so the id must exist BEFORE Start — not after it.
 *
 * A NOTE ON `Initial` AND PROD-1472. This file previously hardcoded Draft with the comment
 * "the backend 500s on `Initial` — verified 2026-07-03 (PROD-1472)". That finding no longer
 * holds: a Flutter network capture on 2026-08-28 shows the open-phase POST — which Flutter
 * sends as `Initial` — returning 201. Treat the July result as stale rather than as a reason
 * to keep sending Draft on open.
 */
export const ENCOUNTER_STATUS_INITIAL = 'Initial'; // AppConstants.encounterStatusInitial
export const ENCOUNTER_STATUS_DRAFT = 'Draft'; // AppConstants.encounterStatusDraft

/** Encounter metadata sourced from the tapped appointment, for CreateEncounter. */
export interface EncounterCreateContext {
  appointmentId: string;
  subjectId: string; // patientId
  serviceProvider?: string; // appointment provider id
  locationName?: string;
  reasonForVisit?: string;
  reasonForVisitName?: string;
}

/** GET the encounter for an appointment. The backend returns 404 when none
 *  exists yet — mirror Flutter (encounter_data_source_impl: 404 → null) so the
 *  caller can branch on `data === null` to create. Do not retry the 404. */
export function useEncounterByAppointment(appointmentId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['encounter-by-appointment', appointmentId],
    queryFn: async ({ signal }): Promise<EncounterModel | null> => {
      try {
        return await get<EncounterModel | null>(
          `/api/Patients/GetEncounterByEncounterId?AppointmentId=${encodeURIComponent(appointmentId!)}`,
          { signal },
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: enabled && !!appointmentId,
    retry: false,
  });
}

/**
 * Local ISO timestamp WITHOUT a timezone suffix — matches Dart's
 * `DateTime.now().toIso8601String()` (e.g. "2026-07-03T22:04:04.784"), which is
 * what Flutter's fresh `EncounterModel(startDate: DateTime.now())` serializes.
 * `Date#toISOString()` would emit UTC + `Z`, diverging from the captured working
 * request — build the local string by hand.
 */
function localIsoNow(): string {
  const d = new Date();
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`
  );
}

/**
 * POST CreateEncounter → the SERVER-assigned encounterId (string).
 *
 * CreateEncounter is an UPSERT keyed on `identifier` (see CreateEncounterRequest).
 * On a fresh create we send `identifier: null` so the backend INSERTs a new row and
 * generates the id, then return that id from the POST response body — exactly as
 * Flutter does (`encounter_data_source_impl.createEncounter` → `response.data.toString()`).
 *
 * PA previously fabricated a client v4 UUID and sent it as `identifier`; the backend
 * took the non-null id as an UPDATE target, found no such row, and 400'd "Encounter
 * not exists" — after which every downstream call used a phantom id (PROD-1480).
 *
 * The null scaffolding fields + empty history arrays are sent EXPLICITLY (present-with-null,
 * not omitted) to match the wire shape; absent context fields are coalesced to `null` so the
 * KEY is still present (an `undefined` would be dropped by JSON.stringify).
 */
export interface CreateEncounterVars extends EncounterCreateContext {
  /**
   * Flutter's `CreateEncounterEvent.status`. `Initial` on open, `Draft` on Start.
   * Defaults to Draft so an un-migrated caller keeps its previous behaviour.
   */
  status?: string;
  /**
   * The row to UPDATE. Null/absent = INSERT a new one (the open phase). Set to the existing
   * encounter id for the Initial → Draft upgrade on Start, which is how Flutter re-posts the
   * SAME model (`model.copyWith(status: Draft)` keeps `identifier`) instead of creating a
   * second encounter. Sending a non-existent id here 400s "Encounter not exists" (PROD-1480).
   */
  identifier?: string | null;
}

export function useCreateEncounter() {
  return useMutation({
    mutationFn: async (ctx: CreateEncounterVars): Promise<string> => {
      const body: CreateEncounterRequest = {
        identifier: ctx.identifier ?? null, // absent → server INSERTs + assigns the id
        status: ctx.status ?? ENCOUNTER_STATUS_DRAFT,
        class: null,
        type: null,
        serviceType: null,
        subject: ctx.subjectId,
        basedOn: null,
        appointment: ctx.appointmentId,
        startDate: localIsoNow(), // Flutter stamps DateTime.now() on the fresh model
        endDate: null,
        createdDate: null,
        duration: 0,
        account: null,
        serviceProvider: ctx.serviceProvider ?? null,
        parentEncounter: null,
        location: null,
        locationName: ctx.locationName ?? null,
        isDeleted: false,
        reasonForVisit: ctx.reasonForVisit ?? null,
        reasonForVisitName: ctx.reasonForVisitName ?? null,
        statusHistoryClass: [],
        historyClass: [],
        encounterLocationClass: [],
        speciality: null,
      };
      // The endpoint returns the new encounter id as a bare/JSON string (Flutter reads
      // response.data.toString()). Use postText and strip any wrapping quotes so we get
      // the raw GUID regardless of whether it's JSON-quoted.
      const raw = await postText('/api/Patients/CreateEncounter', body);
      const id = raw.trim().replace(/^"(.*)"$/s, '$1').trim();
      if (!id) {
        throw new ApiError(502, 'CreateEncounter returned an empty encounter id');
      }
      return id;
    },
  });
}
